import { Routes } from '@angular/router';
import { AccessControls } from './access-controls/components/access-controls/access-controls';
import { PatientAccessDetail } from './access-controls/components/patient-access-detail/patient-access-detail';
import { PatientAccessResults } from './access-controls/components/patient-access-results/patient-access-results';
import { ActivityLogs } from './activity-logs/components/activity-logs/activity-logs';
import { authGuard } from './core/guards/auth.guard';
import { DashboardOverview } from './dashboard/components/dashboard-overview/dashboard-overview';
import { ErrorPageComponent } from './error-page/error-page.component';
import { Login } from './login/login';
import { NewLinkingRequests } from './requests/components/new-linking-requests/new-linking-requests';
import { Unauthorized } from './unauthorized/unauthorized';
import { LinkingRequestDetail } from './requests/components/linking-request-detail/linking-request-detail';
import { SearchPatientRecords } from './requests/components/search-patient-records/search-patient-records';
import { ProxyVerifications } from './requests/components/proxy-verifications/proxy-verifications';
import { ProxyRequestDetail } from './requests/components/proxy-request-detail/proxy-request-detail';
import { AdminActionDetail } from './activity-logs/components/admin-action-detail/admin-action-detail';
import { AppointmentDetail } from './activity-logs/components/appointment-detail/appointment-detail';
import { DisclosureDetail } from './activity-logs/components/disclosure-detail/disclosure-detail';
import { Callback } from './callback/callback';

export const routes: Routes = [
  // The dashboard is the app's landing page: signed-in staff start here, and the
  // "MedOne Admin" brand in the header returns here.
  {
    path: '',
    component: DashboardOverview,
    canActivate: [authGuard],
    pathMatch: 'full',
  },
  {
    path: 'login',
    component: Login,
  },
  {
    path: 'login/callback',
    component: Callback,
  },
  {
    path: 'unauthorized',
    component: Unauthorized,
  },
  {
    path: 'error',
    component: ErrorPageComponent,
  },

  // The Requests nav tab lands directly on the linking-request table.
  {
    path: 'requests',
    component: NewLinkingRequests,
    canActivate: [authGuard],
    pathMatch: 'full',
  },

  {
    path: 'requests/proxy-verifications',
    component: ProxyVerifications,
    canActivate: [authGuard],
  },

  {
    path: 'requests/proxy-verifications/:requestId',
    component: ProxyRequestDetail,
    canActivate: [authGuard],
  },

  {
    path: 'request-detail/:requestId',
    component: LinkingRequestDetail,
    canActivate: [authGuard],
  },

  {
    path: 'request-detail/:requestId/search',
    component: SearchPatientRecords,
    canActivate: [authGuard],
  },

  {
    path: 'activity-logs',
    component: ActivityLogs,
    canActivate: [authGuard],
  },

  {
    path: 'disclosure-detail/:logId',
    component: DisclosureDetail,
    canActivate: [authGuard],
  },

  {
    path: 'admin-action-detail/:logId',
    component: AdminActionDetail,
    canActivate: [authGuard],
  },

  // An appointment disclosure drills down here rather than to `disclosure-detail`: it is the same
  // access-log id and the same endpoint, but appointments render five appointment-specific columns
  // that the generic disclosed-records table can't carry.
  {
    path: 'appointment-detail/:logId',
    component: AppointmentDetail,
    canActivate: [authGuard],
  },

  // The Access & Controls nav tab. The landing page collects patient search
  // criteria and hands off to the results table below, whose Manage Access
  // opens the per-patient detail screen.
  {
    path: 'access-controls',
    component: AccessControls,
    canActivate: [authGuard],
    pathMatch: 'full',
  },

  // The search criteria travel in memory (PatientAccessSearchStateService), not
  // in the URL — they are patient identifiers, and URLs get logged. The screen
  // handles arriving with none by inviting a fresh search.
  {
    path: 'access-controls/patients',
    component: PatientAccessResults,
    canActivate: [authGuard],
    pathMatch: 'full',
  },

  // The detail screen does keep its patient in the URL, unlike the search
  // criteria above: an opaque id is not the identifying free text a name and
  // date of birth are, and carrying it means the screen survives a refresh
  // instead of stranding staff on an empty state.
  {
    path: 'access-controls/patients/:patientId',
    component: PatientAccessDetail,
    canActivate: [authGuard],
  },

  // Legacy URLs: the dashboard lived at /home and then /requests before moving to
  // the root, and the request table lived at /requests/queue. Kept so existing
  // bookmarks and links don't dead-end.
  {
    path: 'home',
    redirectTo: '',
    pathMatch: 'full',
  },

  {
    path: 'requests/queue',
    redirectTo: 'requests',
    pathMatch: 'full',
  },

  // Nothing should hard-fail on an unknown URL; send it to the dashboard and let
  // authGuard decide. Must stay last.
  {
    path: '**',
    redirectTo: '',
  },
];
